import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Gift as GiftIcon, CreditCard, Wallet, Loader2, Heart, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [100, 200, 300, 500];

const Gift = () => {
  const { t, lang } = useLanguage();
  const g = t.gift;
  const [amount, setAmount] = useState(200);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "",
    message: "", delivery_date: "", pdf_lang: "both",
  });
  const [method, setMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);

  const finalAmount = custom ? Number(custom) : amount;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_email || !form.recipient_name || !form.recipient_email) {
      toast.error(g.fillAll); return;
    }
    if (finalAmount < 50 || finalAmount > 2000) { toast.error(g.amountRange); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards/checkout`, {
        amount: finalAmount, ...form, payment_method: method,
        origin_url: window.location.origin, lang,
      });
      if (data.checkout_url) {
        toast.success(g.redirecting);
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || g.error);
    } finally { setSubmitting(false); }
  };

  const inp = "w-full px-5 py-3.5 rounded-xl bg-white border border-[#0D253A]/15 text-sm text-[#1A2129] placeholder:text-[#5C6773]/60";
  const todayIso = new Date().toISOString().split("T")[0];

  return (
    <div data-testid="gift-page" className="min-h-screen pt-28 pb-20 px-6 sm:px-12 lg:px-20 bg-[#F8F5F0]">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {g.eyebrow}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#0D253A]">{g.title}</h1>
          <p className="mt-5 text-base sm:text-lg text-[#333D47] leading-relaxed">{g.lead}</p>
          <div className="mt-8 rounded-2xl overflow-hidden shadow-[0_25px_55px_-25px_rgba(13,37,58,0.4)]">
            <img src="https://images.pexels.com/photos/3649209/pexels-photo-3649209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                 alt="Casa Isolana" className="w-full h-72 object-cover" />
          </div>
          <div className="mt-6 space-y-2 text-sm text-[#333D47]">
            {g.perks.map((p, i) => (
              <p key={i} className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#C86D51] mt-0.5 flex-shrink-0" /> {p}</p>
            ))}
          </div>
        </motion.div>

        <motion.form data-testid="gift-form" onSubmit={submit} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7 bg-white rounded-3xl border border-[#0D253A]/10 p-8 sm:p-10 shadow-[0_25px_60px_-30px_rgba(13,37,58,0.3)] flex flex-col gap-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-3">{g.amountLabel}</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {PRESETS.map((p) => (
                <button key={p} type="button" data-testid={`gift-amount-${p}`} onClick={() => { setAmount(p); setCustom(""); }}
                  className={`px-3 py-3 rounded-xl border text-sm font-semibold transition-colors ${(!custom && amount === p) ? "border-[#C86D51] bg-[#C86D51]/10 text-[#0D253A]" : "border-[#0D253A]/15 text-[#5C6773] hover:border-[#C86D51]/50"}`}>
                  € {p}
                </button>
              ))}
            </div>
            <input data-testid="gift-amount-custom" type="number" step="1" min="50" max="2000" value={custom}
              onChange={(e) => setCustom(e.target.value)} className={inp} placeholder={g.amountCustom} />
            <p className="text-xs text-[#5C6773] mt-1.5">{g.amountRange}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.buyerName}</label>
              <input data-testid="gift-buyer-name" required type="text" value={form.buyer_name} onChange={set("buyer_name")} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.buyerEmail}</label>
              <input data-testid="gift-buyer-email" required type="email" value={form.buyer_email} onChange={set("buyer_email")} className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.recipientName}</label>
              <input data-testid="gift-recipient-name" required type="text" value={form.recipient_name} onChange={set("recipient_name")} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.recipientEmail}</label>
              <input data-testid="gift-recipient-email" required type="email" value={form.recipient_email} onChange={set("recipient_email")} className={inp} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.messageLabel}</label>
            <textarea data-testid="gift-message" maxLength={300} rows={3} value={form.message} onChange={set("message")}
              className={`${inp} resize-none`} placeholder={g.messagePh} />
            <p className="text-xs text-[#5C6773] mt-1">{form.message.length}/300</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.deliveryLabel}</label>
              <input data-testid="gift-delivery-date" type="date" min={todayIso} value={form.delivery_date} onChange={set("delivery_date")} className={inp} />
              <p className="text-xs text-[#5C6773] mt-1">{g.deliveryHint}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">{g.pdfLang}</label>
              <select data-testid="gift-pdf-lang" value={form.pdf_lang} onChange={set("pdf_lang")} className={inp}>
                <option value="both">IT + EN</option>
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-3">{g.payTitle}</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "card", label: g.methods.card, icon: CreditCard },
                { key: "paypal", label: g.methods.paypal, icon: Wallet },
              ].map((m) => {
                const Icon = m.icon;
                const active = method === m.key;
                return (
                  <button type="button" key={m.key} data-testid={`gift-method-${m.key}`} onClick={() => setMethod(m.key)}
                    className={`flex flex-col items-center gap-2 px-3 py-4 rounded-xl border text-xs font-semibold transition-colors ${active ? "border-[#C86D51] bg-[#C86D51]/10 text-[#0D253A]" : "border-[#0D253A]/15 text-[#5C6773] hover:border-[#C86D51]/50"}`}>
                    <Icon className={`w-5 h-5 ${active ? "text-[#C86D51]" : ""}`} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl bg-[#F8F5F0] border border-[#E3DED6] p-5 flex items-end justify-between">
            <span className="text-xs uppercase tracking-wider text-[#5C6773]">{g.total}</span>
            <span data-testid="gift-total" className="font-display text-3xl text-[#0D253A]">€ {(finalAmount || 0).toFixed(2)}</span>
          </div>

          <button data-testid="gift-submit" type="submit" disabled={submitting || finalAmount < 50 || finalAmount > 2000}
            className="mt-2 inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GiftIcon className="w-4 h-4" />}
            {submitting ? g.redirecting : g.cta}
          </button>
          <p className="text-xs text-[#5C6773] leading-relaxed">{g.terms}</p>
        </motion.form>
      </div>
    </div>
  );
};

export default Gift;
